import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";
import { Booking, BookingStatus } from "src/business/booking";
import { DayHoursList } from "src/business/restaurant/restaurant";
import { useBookingsList } from "src/data/booking/queries/booking";
import {
  ServiceHour,
  buildServiceOccupancy,
  computeOccupancyRate,
  totalExpectedSeats,
} from "./serviceOccupancy";

const dayBounds = (day: Dayjs) => ({
  startDate: day.startOf("day").valueOf(),
  endDate: day.endOf("day").valueOf(),
});

/** Fractional change vs. a baseline; `undefined` when there is nothing to compare to. */
const trendAgainst = (current: number, previous: number): number | undefined =>
  previous > 0 ? (current - previous) / previous : undefined;

export interface TodaysService {
  bookings: Booking[];
  bookingTrend: number | undefined;
  expectedSeats: number;
  seatTrend: number | undefined;
  /**
   * Table-slots held over table-slots on offer, 0–100. `null` when the
   * restaurant has no tables on record, or is closed today.
   */
  occupancyRate: number | null;
  pendingCount: number;
  hours: ServiceHour[];
  isLoading: boolean;
}

/**
 * Today's service, plus the same weekday last week as the comparison baseline —
 * a Sunday brunch service is only meaningful against another Sunday.
 *
 * `enabled` is false when the viewer has no access to bookings; the queries are
 * then left unfired rather than 403-ing behind a card that is already explaining
 * the lack of access.
 */
export const useTodaysService = ({
  restaurantId,
  schedule,
  leaves,
  numberOfTables,
  bookingDuration,
  enabled,
}: {
  restaurantId: string;
  schedule: DayHoursList | undefined;
  leaves: string[] | undefined;
  /**
   * Bookable tables — what both the rate and the hourly bars are measured
   * against.
   */
  numberOfTables: number | null;
  /** How long one booking holds its table, in minutes. */
  bookingDuration: number;
  enabled: boolean;
}): TodaysService => {
  const today = dayjs();
  const todayBounds = dayBounds(today);
  const lastWeekBounds = dayBounds(today.subtract(1, "week"));

  // `useBookingsList` derives `enabled` from its dates, so withholding them is
  // how a caller keeps the request from firing.
  const { data: bookings = [], isLoading: todayLoading } = useBookingsList({
    restaurantId,
    startDate: enabled ? todayBounds.startDate : undefined,
    endDate: enabled ? todayBounds.endDate : undefined,
  });

  const { data: lastWeekBookings = [], isLoading: baselineLoading } =
    useBookingsList({
      restaurantId,
      startDate: enabled ? lastWeekBounds.startDate : undefined,
      endDate: enabled ? lastWeekBounds.endDate : undefined,
    });

  const expectedSeats = useMemo(
    () => totalExpectedSeats(bookings),
    [bookings],
  );

  const hours = useMemo(
    () =>
      schedule
        ? buildServiceOccupancy({
            bookings,
            schedule,
            leaves: leaves ?? [],
            date: today.startOf("day"),
            numberOfTables,
            bookingDuration,
          })
        : [],
    // `today` is a fresh Dayjs each render; the day it represents is what
    // matters, so the memo keys on the calendar date instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bookings,
      schedule,
      leaves,
      numberOfTables,
      bookingDuration,
      today.format("YYYY-MM-DD"),
    ],
  );

  const occupancyRate = useMemo(
    () =>
      schedule
        ? computeOccupancyRate({
            bookings,
            schedule,
            leaves: leaves ?? [],
            date: today.startOf("day"),
            numberOfTables,
            bookingDuration,
          })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bookings,
      schedule,
      leaves,
      numberOfTables,
      bookingDuration,
      today.format("YYYY-MM-DD"),
    ],
  );

  return {
    bookings,
    bookingTrend: trendAgainst(bookings.length, lastWeekBookings.length),
    expectedSeats,
    seatTrend: trendAgainst(
      expectedSeats,
      totalExpectedSeats(lastWeekBookings),
    ),
    occupancyRate,
    pendingCount: bookings.filter(
      (booking) => booking.bookingStatus === BookingStatus.PENDING,
    ).length,
    hours,
    isLoading: enabled && (todayLoading || baselineLoading),
  };
};
